import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  listShareLinks, createShareLink, revokeShareLink, getPublicShareUrl,
} from '../utils/api';
import styles from './ShareModal.module.css';

// Per-sheet public-share manager. Lists existing links for the sheet
// (active + revoked, sorted newest first), with Copy + Revoke actions.
// "Generate new link" creates one using the project's default expiration
// days (admin/PM tunes that on the project settings page).

export default function ShareModal({ sheet, project, onClose }) {
  const [links, setLinks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // Optional override for expires_in_days. Defaults to the project setting.
  const [overrideDays, setOverrideDays] = useState('');

  async function refresh() {
    setLoading(true);
    try { setLinks(await listShareLinks(sheet.id)); }
    catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [sheet?.id]);

  async function handleGenerate() {
    setCreating(true);
    try {
      const days = overrideDays && Number(overrideDays) > 0 ? Number(overrideDays) : undefined;
      await createShareLink({ sheet_id: sheet.id, expires_in_days: days });
      toast.success('Share link created');
      setOverrideDays('');
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(link) {
    if (!window.confirm('Revoke this link? Anyone with the URL will get an error from now on.')) return;
    try {
      await revokeShareLink(link.id);
      toast.success('Link revoked');
      await refresh();
    } catch (err) { toast.error(err.message); }
  }

  async function copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied');
    } catch (_) {
      // Fallback for environments without clipboard permission
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success('Link copied'); }
      catch (_) { toast.error('Could not copy'); }
      finally { document.body.removeChild(ta); }
    }
  }

  const defaultDays = project?.share_link_default_days ?? 30;

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <span className={styles.headerTitle}>Share</span>
            <span className={styles.headerSub}>
              {sheet.sheet_number}{sheet.title ? ` · ${sheet.title}` : ''}
            </span>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.body}>
          <p className={styles.blurb}>
            Anyone with the link can view the original PDF (no markups). Links
            expire after <strong>{defaultDays} days</strong> by default —
            change the default in Project Settings or override per link below.
          </p>

          <div className={styles.generateRow}>
            <input
              type="number"
              min="1"
              max="365"
              className={styles.daysInput}
              value={overrideDays}
              onChange={(e) => setOverrideDays(e.target.value)}
              placeholder={`${defaultDays}`}
            />
            <span className={styles.daysLabel}>days</span>
            <button
              className={styles.btnPrimary}
              onClick={handleGenerate}
              disabled={creating}
            >
              {creating ? 'Creating…' : 'Generate new link'}
            </button>
          </div>

          {loading && <div className={styles.empty}>Loading…</div>}

          {!loading && links.length === 0 && (
            <div className={styles.empty}>No share links for this sheet yet.</div>
          )}

          {links.map((link) => (
            <ShareRow key={link.id} link={link} onRevoke={() => handleRevoke(link)} onCopy={copy} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ShareRow({ link, onRevoke, onCopy }) {
  const url = getPublicShareUrl(link.token);
  const status = computeStatus(link);
  const expires = link.expires_at ? format(new Date(link.expires_at), 'MMM d, yyyy h:mm a') : '—';
  return (
    <div className={`${styles.row} ${status === 'active' ? styles.rowActive : styles.rowInactive}`}>
      <div className={styles.rowMain}>
        <div className={styles.rowHeader}>
          <span className={`${styles.statusPill} ${styles['status_' + status]}`}>{statusLabel(status)}</span>
          <span className={styles.rowMeta}>
            Expires {expires}{link.created_by ? ` · created by ${link.created_by}` : ''}
          </span>
        </div>
        <div className={styles.urlRow}>
          <input className={styles.urlInput} value={url} readOnly onFocus={(e) => e.target.select()} />
          <button className={styles.copyBtn} onClick={() => onCopy(url)}>
            <CopyIcon /> Copy
          </button>
        </div>
      </div>
      {status === 'active' && (
        <button className={styles.revokeBtn} onClick={onRevoke}>Revoke</button>
      )}
    </div>
  );
}

function computeStatus(link) {
  if (link.revoked_at) return 'revoked';
  if (link.expires_at && new Date(link.expires_at).getTime() < Date.now()) return 'expired';
  return 'active';
}
function statusLabel(s) {
  return s === 'active' ? 'ACTIVE' : s === 'expired' ? 'EXPIRED' : 'REVOKED';
}

const CopyIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <rect x="4" y="4" width="9" height="10" rx="1" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M3 12V3a1 1 0 011-1h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
